import {
  AlertDialogOverlay,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  HStack,
  useDisclosure,
  useToast,
  Textarea,
} from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useSubmitFundingRequestMutation } from 'types/dashboard-core-gqlTypes.generated.d';

import { CancelFundingRequestButton } from '../shared/CancelFundingRequestButton';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import {
  FundingRequestContextValue,
  ContextValueMutable,
  useFundingRequestTemplateContextV1,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

const SubmitRequestControlsInner = ({
  canCancelOnPending,
  creditFacility: {
    id: creditFacilityId,
    name: creditFacilityName,
    borrowerId,
    capitalProviderRepresentativeOrganization,
  },
  context: {
    submission: { getState, getPayload },
  },
  template,
}: {
  canCancelOnPending?: boolean;
  creditFacility: FundingRequestContextValue['creditFacility'];
  context: ContextValueMutable;
  template: FundingRequestContextValue['template'];
}) => {
  const {
    user: { id: userId },
  } = useUserAuthOrThrow();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);

  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const [comment, setComment] = React.useState<string>('');

  const commentPermissionedOrgs = capitalProviderRepresentativeOrganization?.id
    ? [borrowerId, capitalProviderRepresentativeOrganization.id]
    : [borrowerId];

  const [
    submitFundingRequestMutate,
    { loading: isLoading },
  ] = useSubmitFundingRequestMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      // reverted to toast on all errors for this mutation because customer uses toast as CTA for errors
      errorToastConfig.title =
        err.message ||
        'An unexpected error occurred. Please contact Finley for support.';
      toast(errorToastConfig);
    },
    onCompleted: () => {
      navigate('/funding-requests');
    },
  });

  useEffect(() => {
    if (validationErrors.length > 0) {
      const message = validationErrors.join('\n');
      toast({
        ...errorToastConfig,
        title: message,
      });
    }
  }, [validationErrors, toast]);

  useEffect(() => {
    const validation = getState();
    if (validation.valid) {
      setValidationErrors([]);
    }
  }, [getState]);

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleConfirmSubmit = () => {
    const payload = getPayload();
    const state = getState();

    if (!state.valid) {
      setValidationErrors(state.errors);
      handleClose();
      return;
    }

    // @jendevelops TODO: consolidate error state onto getPayload
    if (!payload.isValid) {
      return;
    }

    submitFundingRequestMutate({
      variables: {
        fundingRequestChangeset: {
          ...payload.payload,
          commentProps: comment
            ? {
                comment,
                organizationIds: commentPermissionedOrgs,
                isPinned: true,
                authorUserId: userId,
              }
            : null,
        },
      },
    });
    handleClose();
  };

  return (
    <HStack
      sx={{
        w: '100%',
        justifyContent: 'flex-end',
        gap: '1em',
      }}
    >
      {template?.currentFundingRequestComponents[0].fundingRequestId ? (
        <CancelFundingRequestButton
          canCancelOnPending={canCancelOnPending}
          fundingRequestStatus="pending"
          fundingRequestId={
            template.currentFundingRequestComponents[0].fundingRequestId
          }
          creditFacilityId={creditFacilityId}
          isDisabled={isLoading}
        />
      ) : (
        <Button
          variant="secondary-muted"
          colorScheme="v2-aurora"
          onClick={() => navigate('/funding-requests')}
        >
          Cancel
        </Button>
      )}
      <Button
        variant="primary"
        colorScheme="v2-aurora"
        isLoading={isLoading}
        onClick={onOpen}
      >
        Submit
      </Button>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Submit Funding Request
            </AlertDialogHeader>

            <AlertDialogBody>
              {commonUtils.EnvUtils.isProduction() ? (
                <Typography variant="base">{`Are you sure you want to submit the Funding Request for ${creditFacilityName}?`}</Typography>
              ) : (
                <>
                  <Typography variant="base">{`Are you sure you want to submit the Funding Request for ${creditFacilityName}? If so, you can include a message that will be
              attached in the email.`}</Typography>
                  <Textarea
                    mt="1rem"
                    value={comment}
                    placeholder="Write a message"
                    onChange={(e) => setComment(e.target.value)}
                  />
                </>
              )}
            </AlertDialogBody>

            <AlertDialogFooter sx={{ gap: '1em' }}>
              <Button
                variant="secondary-muted"
                colorScheme="v2-aurora"
                ref={cancelRef}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                colorScheme="v2-aurora"
                onClick={handleConfirmSubmit}
                isLoading={isLoading}
              >
                Submit
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </HStack>
  );
};

export const SubmitRequestControls = ({
  canCancelOnPending,
}: {
  canCancelOnPending?: boolean;
}) => {
  const {
    context,
    creditFacility,
    template,
  } = useFundingRequestTemplateContextV1();

  return context.allowedActions === 'mutable' ? (
    <SubmitRequestControlsInner
      context={context}
      creditFacility={creditFacility}
      template={template}
      canCancelOnPending={canCancelOnPending}
    />
  ) : null;
};
